import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import AdminLayout from '../components/AdminLayout'
import AdminDateRangePicker, { IsoRange } from '../components/AdminDateRangePicker'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'
import { useUserProfile } from '../context/UserProfileContext'

type DashboardData = {
  revenue?: number
  customers?: { total?: number; active?: number }
  filings?: { total?: number; ct600?: number; selfAssessment?: number }
  filingsByModule?: {
    corporationTax?: number
    companiesHouse?: number
    both?: number
    selfAssessment?: number
    confirmationStatement?: number
    total?: number
  }
  companies?: { total?: number; active?: number; suspended?: number; upcomingDeadlinesThisMonth?: number; failedFilings?: number }
  recent?: Array<{ type?: string; title?: string; id?: string; status?: string; createdAt?: string }>
}

export default function AdminDashboardPage() {
  const [data, setData] = useState<DashboardData>({})
  const [filings, setFilings] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState<IsoRange>({})
  const { getUserDisplayName } = useUserProfile()

  const rangeStart = range.start
  const rangeEnd = range.end

  useEffect(() => {
    const loadDashboard = async () => {
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.getDashboard({ start: rangeStart, end: rangeEnd })
        const payload: DashboardData = (response as any)?.data || response
        setData(payload)

        setFilings((payload.recent || []).map((item, idx) => ({
          type: item.type || 'CT600',
          entity: item.title || '—',
          period: (item as any).taxYear || item.createdAt || '—',
          dueDate: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
          status: item.status || 'Submitted',
          key: item.id || idx,
        })))
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load dashboard.')
      } finally {
        setLoading(false)
      }
    }

    void loadDashboard()
  }, [rangeStart, rangeEnd])
  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10'>
        {/* Top bar */}
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900 break-words'>Hi, {getUserDisplayName()}</h1>
            <p className='text-sm text-gray-500'>You are welcome</p>
          </div>
          <div className='flex items-center gap-2 sm:gap-3 flex-wrap'>
            <AdminDateRangePicker onChange={setRange} />
          </div>
        </div>

        {/* Stats */}
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 mb-6'>
          {loading
            ? Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className='rounded-2xl bg-white p-4 sm:p-5 border border-gray-100 shadow-sm animate-pulse'>
                  <div className='h-3 w-1/3 rounded bg-gray-200 mb-3' />
                  <div className='h-8 w-2/3 rounded bg-gray-200' />
                </div>
              ))
            : [
                data.revenue && Number(data.revenue) > 0
                  ? { label: 'Income', value: `£${Number(data.revenue).toLocaleString()}`, delta: '' }
                  : { label: 'Total Companies', value: data.companies?.total ?? 0, delta: '' },
                { label: 'Total Users', value: data.customers?.total || 0, delta: '' },
                { label: 'Total Filings', value: data.filings?.total || 0, delta: '' },
              ].map((card, idx) => (
                <div key={idx} className='rounded-2xl bg-white p-4 sm:p-5 border border-gray-100 shadow-sm'>
                  <p className='text-sm font-medium text-gray-500 truncate mb-2'>{card.label}</p>
                  <p className='text-2xl sm:text-3xl font-bold text-gray-900 break-words'>{card.value}</p>
                  {card.delta && (
                    <div className='mt-2 text-sm text-gray-600'>
                      <span className='text-emerald-600 font-semibold'>{card.delta}</span> Since Last Month
                    </div>
                  )}
                </div>
              ))}
        </div>

        {error && (
          <div className='mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        {/* Filings by module */}
        <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5 mb-6'>
          <p className='text-sm font-semibold text-gray-800 mb-4'>Filings by Module</p>
          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
            {[
              { label: 'Corporation Tax', value: data.filingsByModule?.corporationTax ?? 0 },
              { label: 'Companies House', value: data.filingsByModule?.companiesHouse ?? 0 },
              { label: 'Both (CT & Accounts)', value: data.filingsByModule?.both ?? 0 },
              { label: 'Self Assessment', value: data.filingsByModule?.selfAssessment ?? 0 },
              { label: 'Confirmation Statement', value: data.filingsByModule?.confirmationStatement ?? 0 },
            ].map(tile => (
              <div key={tile.label} className='rounded-xl border border-gray-100 bg-gray-50 p-3 sm:p-4'>
                <p className='text-xs font-medium text-gray-500 leading-tight min-h-[2rem]'>{tile.label}</p>
                <p className='mt-1 text-2xl font-bold text-gray-900'>
                  {loading ? <span className='inline-block h-6 w-10 rounded bg-gray-200 animate-pulse align-middle' /> : tile.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Filings - Full width */}
        <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3'>
            <p className='text-sm font-semibold text-gray-800'>Recent Filing</p>
            <div className='flex items-center gap-2 sm:gap-3 flex-wrap'>
              <div className='flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 flex-1 sm:flex-none min-w-0'>
                <Search className='h-4 w-4 text-gray-400 flex-shrink-0' />
                <input
                  placeholder='Search...'
                  className='bg-transparent outline-none placeholder:text-gray-400 text-sm w-full min-w-0'
                />
              </div>
              <button className='rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex-shrink-0'>
                Export
              </button>
            </div>
          </div>
          <div className='overflow-x-auto rounded-xl border border-gray-100'>
            <table className='w-full min-w-[640px] text-sm text-gray-700'>
              <thead className='bg-gray-50 text-left text-xs font-semibold text-gray-500'>
                <tr>
                  <th className='px-4 py-3 whitespace-nowrap'>Type</th>
                  <th className='px-4 py-3 whitespace-nowrap'>Entity</th>
                  <th className='px-4 py-3 whitespace-nowrap'>Period</th>
                  <th className='px-4 py-3 whitespace-nowrap'>Due Date</th>
                  <th className='px-4 py-3 whitespace-nowrap'>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, rowIdx) => (
                    <tr key={`skel-${rowIdx}`} className='border-t border-gray-100'>
                      {Array.from({ length: 5 }).map((__, colIdx) => (
                        <td key={colIdx} className='px-4 py-3'>
                          <div className='h-3 rounded bg-gray-200 animate-pulse' style={{ width: `${55 + ((rowIdx + colIdx) % 4) * 10}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filings.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='px-4 py-10 text-center text-sm text-gray-500'>
                      No recent filings.
                    </td>
                  </tr>
                ) : (
                  filings.map(item => (
                    <tr key={item.key} className='border-t border-gray-100'>
                      <td className='px-4 py-3 text-start whitespace-nowrap'>{item.type}</td>
                      <td className='px-4 py-3 text-start whitespace-nowrap'>{item.entity}</td>
                      <td className='px-4 py-3 text-start whitespace-nowrap'>{item.period}</td>
                      <td className='px-4 py-3 text-start whitespace-nowrap'>{item.dueDate}</td>
                      <td className='px-4 py-3 text-start whitespace-nowrap'>
                        <span className='inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100'>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
